import React, { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams, useLocation } from 'react-router-dom'

import NavigationBar from '../components/NavigationBar'
import Footer from '../components/Footer/Footer'
import { useLanguage } from '../contexts/LanguageContext'
import { MANAGE_EVENT_EDIT_PATH, MANAGE_POI_EDIT_PATH } from '../config/routes'
import { usePois } from '../contexts/PoisContext'
import { AuthContext } from '../contexts/AuthContext'

import PoiStatus from '../components/PointOfInterest/PoiStatus'
import GoogleMap from '../components/PointOfInterest/GoogleMap'
import PoiTechnicalSheet from '../components/PointOfInterest/PoiTechnicalSheet'
import MainPhoto from '../components/MainPhoto'
import OthersPhotos from '../components/OthersPhotos'
import PoiVideo from '../components/PointOfInterest/PoiVideo'
import VillageEvents from '../components/PointOfInterest/VillageEvents'
import SameCityPois from '../components/PointOfInterest/SameCityPois'
import PoiInternalLinks from '../components/PointOfInterest/PoiInternalLinks'
import PoiExternalLinks from '../components/PointOfInterest/PoiExternalLinks'
import PoiAffiliateLinks from '../components/PointOfInterest/PoiAffiliateLinks'
import PoiComments from '../components/PointOfInterest/PoiComments'
import Courbe from '../components/Courbe'
import PoiTypeThumbnail from '../components/PointOfInterest/PoiTypeThumbnail'
import PopulationHistory from '../components/PointOfInterest/PopulationHistory'
import BeforeAftersViewer from '../components/PointOfInterest/BeforeAftersViewer'

import config from '../config/config'
import { slugify, getPoiLabel, getPoiFullLabel } from '../utils/utils'
import { formatDate } from '../utils/dateUtils'

const PointOfInterest = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { id } = useParams()
  const { activeLanguage } = useLanguage()
  const { pois } = usePois()
  const { isAdmin } = useContext(AuthContext)

  const poi = pois.find((p) => `${p.id}` === id)

  useEffect(() => {
    if (poi) {
      document.title = getPoiFullLabel(poi, activeLanguage)
    }
  }, [poi, activeLanguage])

  if (!poi) return null

  const getPoiDescription = (poi) => {
    return activeLanguage === 'en'
      ? poi.descriptionEn
      : activeLanguage === 'es'
      ? poi.descriptionEs
      : poi.descriptionFr
  }

  const startDate = new Date(poi.startDate)
  const isStartAtMidnight =
    startDate.getHours() === 0 && startDate.getMinutes() === 0
  const withTime = !isStartAtMidnight

  return (
    <div>
      <NavigationBar />

      <div>
        <div className="hero text-center py-5">
          <h1 className="mb-3">{getPoiLabel(poi, activeLanguage)}</h1>
          <p>
            {poi.isEvent && (
              <>
                {poi.endDate
                  ? `${t('FROM_DATE')} ${formatDate(
                      poi.startDate,
                      withTime,
                    )} ${t('TO_DATE')} ${formatDate(poi.endDate, withTime)}`
                  : formatDate(poi.startDate, withTime)}
              </>
            )}
          </p>
        </div>

        <div className="p-3">
          {isAdmin && (
            <Link
              to={`${(poi.isEvent
                ? MANAGE_EVENT_EDIT_PATH
                : MANAGE_POI_EDIT_PATH
              )
                .replace(':slug', slugify(poi.labelFr))
                .replace(':id', poi.id)}`}
              className="btn btn-primary"
              style={{ margin: '10px' }}
            >
              {t('MANAGE')}
            </Link>
          )}

          <PoiStatus poi={poi} />
          <PoiTechnicalSheet poi={poi} isButtonShow={false} />
          <MainPhoto item={poi} />

          <div className="container section py-4">
            <p className="lead">{getPoiDescription(poi)}</p>
          </div>

          <GoogleMap poi={poi} />

          {poi.poiType.name === 'VILLAGE' && (
            <>
              <SameCityPois
                poi={poi}
                titleKey="SERVICES"
                acceptedTypes={['SERVICE', 'PICNIC', 'WATER_POINT']}
              />
              <VillageEvents poi={poi} />
              <SameCityPois
                poi={poi}
                titleKey="TODOS"
                acceptedTypes={['WALK', 'TRAIL', 'HIKING', 'CLIMBING']}
              />
              <SameCityPois
                poi={poi}
                titleKey="CYCLING"
                acceptedTypes={['MOUNTAIN_BIKING', 'ROAD_CYCLING']}
              />
              <SameCityPois
                poi={poi}
                titleKey="TOSEES"
                acceptedTypes={['CURIOSITY', 'WALK']}
              />
              <SameCityPois
                poi={poi}
                titleKey="HISTORY"
                acceptedTypes={['HISTORY']}
              />
              <SameCityPois
                poi={poi}
                titleKey="RAMPAGE"
                acceptedTypes={['RAMPAGE']}
              />
            </>
          )}

          <Courbe poi={poi} />
          <PoiVideo poi={poi} />

          {poi.poiType.name === 'HISTORY' &&
            location.pathname.startsWith('/point-d-interet/histoire-d') && (
              <PopulationHistory poi={poi} />
            )}

          <BeforeAftersViewer beforeAfters={poi.beforeAfters} />
          <OthersPhotos item={poi} />

          {config.poiTypesWithThumbnail.includes(poi.poiType.name) && (
            <div className="mt-4">
              <hr />
              <h2 className="mb-4">{t('SAME_CATEGORY')}</h2>
              <div className="row g-4">
                <div className="col-md-6 col-lg-4 d-flex">
                  <PoiTypeThumbnail poiType={poi.poiType} />
                </div>
              </div>
            </div>
          )}

          <PoiInternalLinks poi={poi} />
          <PoiExternalLinks poi={poi} />
          <PoiAffiliateLinks poi={poi} />
          <PoiComments poi={poi} />
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default PointOfInterest
